/**
 * Recognising a newer deployment of a web app while a tab is still open.
 *
 * HOW A NEW VERSION IS RECOGNISED
 * -------------------------------
 * Vite names the entry bundle after its content (`/assets/index-<hash>.js`) and the hash
 * folds in every chunk the entry references, so ANY code change of an app changes ITS entry
 * file name and nothing else does. A running page compares two things:
 *
 *   · the entry module script of the document it is running (what the tab loaded), and
 *   · the entry module script of the `index.html` the server hands out now.
 *
 * Different → a newer build is live. This needs no build step and no version file, and it
 * follows one app's bundle only: a deployment that touches another app of the monorepo
 * (same repo, same push) leaves this entry untouched and raises nothing.
 *
 * Everything unknown (offline, a 403 from the host's bot check, an HTML without an entry
 * script) resolves to `null` and is read as «no news»: a notice is only ever raised on
 * positive evidence, never on a failed request.
 *
 * Pure on purpose (no DOM, no network): the browser half — reading the running document and
 * fetching `index.html` — lives in the web UI package next to the notice that uses it.
 */

const SCRIPT_TAG = /<script\b[^>]*>/gi;
const TYPE_MODULE = /\btype\s*=\s*(?:"module"|'module'|module\b)/i;
const SRC_ATTR = /\bsrc\s*=\s*(?:"([^"]*)"|'([^']*)')/i;

/**
 * The pathname of a same-origin script `src`, or `null` for a foreign or unparsable one.
 * Compared as a pathname so `https://host/assets/x.js` and `/assets/x.js` are the same file.
 */
export function entryPathOf(src: string, baseUrl: string): string | null {
  try {
    const base = new URL(baseUrl);
    const url = new URL(src, base);
    return url.origin === base.origin ? url.pathname : null;
  } catch {
    return null;
  }
}

/** The first same-origin `<script type="module" src>` of an HTML text (Vite's entry). */
export function entryScriptFromHtml(html: string, baseUrl: string): string | null {
  for (const tag of html.match(SCRIPT_TAG) ?? []) {
    if (!TYPE_MODULE.test(tag)) continue;
    const match = SRC_ATTR.exec(tag);
    const src = match?.[1] ?? match?.[2];
    if (src === undefined) continue;
    const path = entryPathOf(src, baseUrl);
    if (path !== null) return path;
  }
  return null;
}

/** A newer build is live only when BOTH entries are known and they differ. */
export function isNewVersion(current: string | null, latest: string | null): boolean {
  return current !== null && latest !== null && current !== latest;
}
